import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, MapPin, Plus, RefreshCw, Save, Trash2, X } from "lucide-react";

import { api } from "../lib/api";
import type { OrgUnit } from "../lib/types";
import MapPicker from "./MapPicker";

const STATUS_OPTIONS = ["home", "relocated", "in_transit", "unknown"] as const;

interface EditableEmployee {
  oracle_id: string;
  first?: string;
  middle?: string;
  last?: string;
  full_name?: string;
  nationality?: string | null;
  primary_phone?: string | null;
  email?: string | null;
  home: {
    country?: string | null;
    city?: string | null;
    address?: string | null;
    lat?: number | null;
    lng?: number | null;
    lat_lng_manual?: boolean;
  };
  org: {
    unit_id?: string | null;
    unit_name?: string | null;
    function?: string | null;
    title?: string | null;
    manager_oracle_id?: string | null;
    manager_name?: string | null;
  };
  emergency_contact_name?: string | null;
  emergency_contact_relation?: string | null;
  red_flags?: string[];
  current?: { status: string };
}

interface Props {
  employee: EditableEmployee;
  onClose: () => void;
  /** When true, creates via POST /api/employees instead of PATCH. The
   * `oracle_id` becomes editable. */
  mode?: "edit" | "create";
}

export default function EmployeeEditor({ employee, onClose, mode = "edit" }: Props) {
  const qc = useQueryClient();
  const unitsQ = useQuery({
    queryKey: ["org-units"],
    queryFn: () => api.get<{ items: OrgUnit[] }>("/api/org/units"),
  });

  const [oracleId, setOracleId] = useState(employee.oracle_id ?? "");
  const [first, setFirst] = useState(employee.first ?? "");
  const [middle, setMiddle] = useState(employee.middle ?? "");
  const [last, setLast] = useState(employee.last ?? "");
  const [fullName, setFullName] = useState(employee.full_name ?? "");
  const [nationality, setNationality] = useState(employee.nationality ?? "");
  const [phone, setPhone] = useState(employee.primary_phone ?? "");
  const [email, setEmail] = useState(employee.email ?? "");
  const [homeCountry, setHomeCountry] = useState(employee.home.country ?? "");
  const [homeCity, setHomeCity] = useState(employee.home.city ?? "");
  const [homeAddress, setHomeAddress] = useState(employee.home.address ?? "");
  const [unitId, setUnitId] = useState(employee.org.unit_id ?? "");
  const [orgFunction, setOrgFunction] = useState(employee.org.function ?? "");
  const [title, setTitle] = useState(employee.org.title ?? "");
  const [emName, setEmName] = useState(employee.emergency_contact_name ?? "");
  const [emRelation, setEmRelation] = useState(employee.emergency_contact_relation ?? "");
  const [redFlags, setRedFlags] = useState<string[]>(employee.red_flags ?? []);
  const [newFlag, setNewFlag] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Manual lat/lng override. `null` = let the backend re-derive from
  // city/country (default behaviour). A number = pin the marker exactly
  // here, regardless of address.
  const [homeLat, setHomeLat] = useState<number | null>(
    employee.home.lat ?? null,
  );
  const [homeLng, setHomeLng] = useState<number | null>(
    employee.home.lng ?? null,
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  // Track whether the lat/lng was *explicitly* changed by the user since
  // opening the editor. We only send manual coords to the backend when
  // it was, to keep auto-recompute working when the address changes.
  const [coordsTouched, setCoordsTouched] = useState(false);
  const initialManual = employee.home.lat_lng_manual === true;
  // Show "manual" badge if the saved value is manual, OR if the user just
  // pinned a new location in this editor session.
  const isManual = coordsTouched
    ? homeLat != null && homeLng != null
    : initialManual;

  // Auto-derive full_name when components change unless user edited it manually.
  const [autoFullName, setAutoFullName] = useState(true);
  useEffect(() => {
    if (!autoFullName) return;
    setFullName([first, middle, last].filter(Boolean).join(" ").trim());
  }, [first, middle, last, autoFullName]);

  const save = useMutation({
    mutationFn: async () => {
      const selectedUnit = unitsQ.data?.items.find((u) => u.id === unitId);
      const basePayload: Record<string, unknown> = {
        first,
        middle,
        last,
        full_name: fullName,
        nationality: nationality || null,
        primary_phone: phone || null,
        email: email || null,
        home: {
          country: homeCountry || null,
          city: homeCity || null,
          address: homeAddress || null,
          // Only send lat/lng when the user explicitly touched them; the
          // backend will otherwise auto-derive from city/country.
          ...(coordsTouched
            ? { lat: homeLat, lng: homeLng }
            : {}),
        },
        org: {
          unit_id: unitId || null,
          unit_name: selectedUnit?.name ?? null,
          function: orgFunction || selectedUnit?.function || null,
          title: title || null,
        },
        emergency_contact_name: emName || null,
        emergency_contact_relation: emRelation || null,
        red_flags: redFlags,
      };
      if (mode === "create") {
        const id = oracleId.trim();
        if (!id) throw new Error("Oracle ID is required");
        return api.post(`/api/employees`, { ...basePayload, oracle_id: id });
      }
      return api.patch(
        `/api/employees/${encodeURIComponent(employee.oracle_id)}`,
        basePayload,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employee", employee.oracle_id] });
      qc.invalidateQueries({ queryKey: ["employees"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      onClose();
    },
    onError: (e: Error) => setError(e.message || "Save failed"),
  });

  return (
    <div
      className="fixed inset-0 z-[1100] flex items-start justify-center bg-black/60 backdrop-blur-sm overflow-y-auto py-4 sm:py-8 px-2 sm:px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl rounded-2xl bg-[var(--color-surface)] ring-1 ring-[var(--color-border)] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800">
          <div>
            <div className="text-sm uppercase tracking-wide text-slate-400">
              {mode === "create" ? "New employee" : "Edit employee"}
            </div>
            <div className="text-lg font-semibold text-slate-100">
              {fullName || employee.full_name || employee.oracle_id || "—"}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-800"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
          {mode === "create" && (
            <Field label="Oracle ID *" full>
              <Input
                value={oracleId}
                onChange={setOracleId}
                placeholder="e.g. 123456 — unique employee identifier"
              />
            </Field>
          )}
          <Field label="First name">
            <Input value={first} onChange={setFirst} />
          </Field>
          <Field label="Middle name">
            <Input value={middle} onChange={setMiddle} />
          </Field>
          <Field label="Last name">
            <Input value={last} onChange={setLast} />
          </Field>
          <Field label="Full name (display)">
            <Input
              value={fullName}
              onChange={(v) => {
                setAutoFullName(false);
                setFullName(v);
              }}
            />
          </Field>
          <Field label="Nationality">
            <Input value={nationality} onChange={setNationality} placeholder="Israeli, Indian, …" />
          </Field>
          <Field label="Primary phone (E.164)">
            <Input value={phone} onChange={setPhone} placeholder="+9715…" />
          </Field>
          <Field label="Email">
            <Input value={email} onChange={setEmail} type="email" />
          </Field>
          <Field label="Title">
            <Input value={title} onChange={setTitle} />
          </Field>

          <Field label="Org unit" full>
            <select
              value={unitId}
              onChange={(e) => setUnitId(e.target.value)}
              className="w-full rounded-lg bg-slate-900/80 ring-1 ring-slate-700 px-3 py-2 text-slate-100 outline-none focus:ring-sky-500"
            >
              <option value="">— Unassigned —</option>
              {unitsQ.data?.items.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name} {u.function ? `· ${u.function}` : ""}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Function override">
            <Input
              value={orgFunction}
              onChange={setOrgFunction}
              placeholder="Inherit from unit by default"
            />
          </Field>
          <Field label="Current status (read-only)">
            <select
              disabled
              value={employee.current?.status || "unknown"}
              className="w-full rounded-lg bg-slate-900/40 ring-1 ring-slate-800 px-3 py-2 text-slate-400 outline-none"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Home country">
            <Input value={homeCountry} onChange={setHomeCountry} />
          </Field>
          <Field label="Home city">
            <Input value={homeCity} onChange={setHomeCity} />
          </Field>
          <Field label="Home address" full>
            <Input value={homeAddress} onChange={setHomeAddress} />
          </Field>

          <Field label="Map location" full>
            <div className="rounded-lg ring-1 ring-slate-700 bg-slate-900/60 p-3 space-y-2">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-slate-400 inline-flex items-center gap-2">
                  <MapPin size={14} className="text-sky-400" />
                  {homeLat != null && homeLng != null ? (
                    <span className="tabular-nums text-slate-200">
                      {homeLat.toFixed(5)}, {homeLng.toFixed(5)}
                      {isManual ? (
                        <span className="ml-2 text-amber-300">manual</span>
                      ) : (
                        <span className="ml-2 text-slate-500">auto</span>
                      )}
                    </span>
                  ) : (
                    <span className="text-slate-500">
                      No coordinates yet — will be derived from city/country.
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setPickerOpen(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-sky-500 hover:bg-sky-400 text-white text-xs font-medium"
                  >
                    <MapPin size={13} /> Pick on map
                  </button>
                  {isManual && (
                    <button
                      type="button"
                      onClick={() => {
                        setHomeLat(null);
                        setHomeLng(null);
                        setCoordsTouched(true);
                      }}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs ring-1 ring-slate-700 text-slate-300 hover:bg-slate-800"
                      title="Clear manual coordinates and let the system recompute from address"
                    >
                      <RefreshCw size={12} /> Auto from address
                    </button>
                  )}
                </div>
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                The pin is normally derived from the home city &amp; country.
                Use <span className="text-slate-300">Pick on map</span> when
                the auto-derived location is wrong (e.g. "Damascus Street"
                resolving to Syria instead of Dubai).
              </p>
            </div>
          </Field>

          <Field label="Emergency contact name">
            <Input value={emName} onChange={setEmName} />
          </Field>
          <Field label="Relation">
            <Input value={emRelation} onChange={setEmRelation} />
          </Field>

          <Field label="Red flags" full>
            <div className="space-y-2">
              <ul className="space-y-1">
                {redFlags.map((f, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-2 rounded-md bg-amber-500/10 ring-1 ring-amber-400/30 px-2 py-1 text-sm text-amber-100"
                  >
                    <span className="flex-1">{f}</span>
                    <button
                      type="button"
                      onClick={() => setRedFlags(redFlags.filter((_, j) => j !== i))}
                      className="text-amber-300 hover:text-amber-100"
                    >
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
              <div className="flex gap-2">
                <Input
                  value={newFlag}
                  onChange={setNewFlag}
                  placeholder="Add a red flag (e.g. expired residency)…"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (!newFlag.trim()) return;
                    setRedFlags([...redFlags, newFlag.trim()]);
                    setNewFlag("");
                  }}
                  className="inline-flex items-center gap-1 px-3 rounded-lg bg-slate-800 hover:bg-slate-700 ring-1 ring-slate-700 text-slate-200 text-sm"
                >
                  <Plus size={14} /> Add
                </button>
              </div>
            </div>
          </Field>
        </div>

        {error && (
          <div className="mx-5 mb-3 text-sm text-red-300 bg-red-500/10 ring-1 ring-red-500/30 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-800">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-slate-300 hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-400 disabled:opacity-50 text-white text-sm font-medium"
          >
            {save.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Save size={14} />
            )}
            Save
          </button>
        </div>
      </div>

      {pickerOpen && (
        <MapPicker
          lat={homeLat}
          lng={homeLng}
          onClose={() => setPickerOpen(false)}
          onPick={(lat, lng) => {
            setHomeLat(lat);
            setHomeLng(lng);
            setCoordsTouched(true);
            setPickerOpen(false);
          }}
        />
      )}

    </div>
  );
}

function Field({
  label,
  children,
  full,
}: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
}) {
  return (
    <label className={"block " + (full ? "sm:col-span-2" : "")}>
      <span className="text-xs uppercase tracking-wide text-slate-400">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Input({
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-lg bg-slate-900/80 ring-1 ring-slate-700 px-3 py-2 text-slate-100 outline-none focus:ring-sky-500"
    />
  );
}
